import React, { useEffect, useRef, useState } from 'react';

export default function RGBBackground() {
  const canvasRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: -100, y: -100 });
  const [targetMousePos, setTargetMousePos] = useState({ x: -100, y: -100 });

  // Mouse Follower easing (lag effect for smooth organic movement)
  useEffect(() => {
    let animationId;
    const handleMouseMove = (e) => {
      setTargetMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);

    const updateMousePos = () => {
      setMousePos((prev) => {
        const dx = targetMousePos.x - prev.x;
        const dy = targetMousePos.y - prev.y;
        // Adjust the multiplier for more or less slide/lag
        return {
          x: prev.x + dx * 0.1,
          y: prev.y + dy * 0.1,
        };
      });
      animationId = requestAnimationFrame(updateMousePos);
    };

    animationId = requestAnimationFrame(updateMousePos);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
    };
  }, [targetMousePos]);

  // Canvas floating RGB particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle Class
    class Particle {
      constructor() {
        this.reset();
        // Give some initial random vertical offset on first load
        this.y = Math.random() * canvas.height;
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height + 20;
        this.size = Math.random() * 2 + 1;
        this.speedY = -(Math.random() * 0.6 + 0.2);
        this.speedX = (Math.random() * 0.4 - 0.2);
        
        // Cyber Neon colors
        const colors = [
          'rgba(0, 240, 255, ', // cyan
          'rgba(189, 0, 255, ', // purple
          'rgba(255, 0, 122, ', // pink
          'rgba(57, 255, 20, '   // green
        ];
        this.colorBase = colors[Math.floor(Math.random() * colors.length)];
        this.alpha = Math.random() * 0.4 + 0.1;
        this.maxAlpha = this.alpha;
      }

      update() {
        this.y += this.speedY;
        this.x += this.speedX;

        // Fade out near the top
        if (this.y < 100) {
          this.alpha = (this.y / 100) * this.maxAlpha;
        }

        if (this.y < -10 || this.x < -10 || this.x > canvas.width + 10) {
          this.reset();
        }
      }

      draw() {
        ctx.fillStyle = `${this.colorBase}${this.alpha})`;
        ctx.shadowBlur = this.size * 3;
        ctx.shadowColor = `${this.colorBase}0.5)`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0; // Reset shadow for efficiency
      }
    }

    const particleCount = Math.min(60, Math.floor((window.innerWidth * window.innerHeight) / 30000));
    const particles = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className="fixed inset-0 w-full h-full pointer-events-none overflow-hidden z-0 bg-[#030305]">
      {/* 3D Moving Perspective Grid Overlay */}
      <div className="absolute inset-0 bg-cyber-grid bg-repeat opacity-[0.05] animate-grid-move transform-gpu"></div>
      <div className="absolute inset-0 bg-cyber-dots bg-repeat opacity-[0.06]"></div>

      {/* Cyberpunk perspective horizontal fade line (horizon glow) */}
      <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#00f0ff] to-transparent shadow-[0_-5px_20px_rgba(0,240,255,0.8)] opacity-20"></div>

      {/* Ambient Moving RGB Orbs (Layered blur lights) */}
      <div className="absolute top-[10%] left-[5%] w-[45vw] h-[45vw] rounded-full bg-gradient-to-br from-cyber-blue/10 to-transparent blur-[120px] animate-orb-drift"></div>
      <div className="absolute bottom-[10%] right-[5%] w-[50vw] h-[50vw] rounded-full bg-gradient-to-tr from-cyber-purple/8 to-transparent blur-[150px] animate-orb-drift-delayed"></div>
      <div className="absolute top-[40%] right-[15%] w-[35vw] h-[35vw] rounded-full bg-gradient-to-bl from-cyber-pink/6 to-transparent blur-[110px] animate-orb-drift"></div>
      <div className="absolute bottom-[30%] left-[20%] w-[40vw] h-[40vw] rounded-full bg-gradient-to-br from-cyber-green/5 to-transparent blur-[130px] animate-orb-drift-delayed"></div>

      {/* Interactive Cyber Mouse Follower Glow (Glow trail) */}
      <div
        className="absolute w-[300px] h-[300px] rounded-full bg-radial-gradient from-cyber-blue/15 via-cyber-purple/5 to-transparent blur-[80px] -translate-x-1/2 -translate-y-1/2 will-change-transform transform-gpu opacity-70"
        style={{
          left: `${mousePos.x}px`,
          top: `${mousePos.y}px`,
          background: 'radial-gradient(circle, rgba(0,240,255,0.12) 0%, rgba(189,0,255,0.04) 50%, transparent 100%)',
        }}
      ></div>

      {/* Canvas for fine neon spark particles */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
    </div>
  );
}
